import { ListsInclusions } from '@app/data/clients/rbServerSideClient/queries/GetListInclusions/types';
import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import { withErrorBoundary } from '@redbubble/next/error-boundary';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AnalyticsItemListID } from '@redbubble/next-analytics/gtm';
import React from 'react';
import { SearchUUIDStorageKeyId } from 'utils/searchUUIDStorage';
import { useAddToList } from '../../hooks/useAddToList';
import { trackGA4AddToWishListEvent } from '../../pages/i/analytics';
import { ListsButtonInventoryItem } from '../../types/shop-bff/types';
import { FavoriteIcon } from '../FavoriteIcon';

export const messages = defineMessages({
  addToList: {
    defaultMessage: 'Add to list',
  },
  removeFromList: {
    defaultMessage: 'Remove from list',
  },
});

export interface AddToListButtonProps {
  inventoryItem: ListsButtonInventoryItem | ProcessedSearchResultItem;
  workId: string;
  analyticsComponentID: AnalyticsItemListID;
  analyticsIndex: number;
  searchUUIDStorageKey?: SearchUUIDStorageKeyId;
  onAddToList?: () => void;
  listsInclusions: ListsInclusions | null;
  size?: string;
  elevation?: string;
}

export const AddToListButton: React.FC<AddToListButtonProps> =
  withErrorBoundary(
    ({
      inventoryItem,
      workId,
      analyticsComponentID,
      analyticsIndex,
      searchUUIDStorageKey,
      onAddToList,
      listsInclusions,
      size = constants.SMALL,
      elevation = constants.ELEVATION_MEDIUM,
    }) => {
      const intl = useIntl();

      const handleAddToList = () => {
        trackGA4AddToWishListEvent({
          item: inventoryItem,
          workId,
          analyticsComponentID,
          itemIndex: analyticsIndex,
          searchUUIDStorageKey,
        });

        if (onAddToList) {
          onAddToList();
        }
      };

      const { itemIsFavorited, handleOnToggleList, ListModal } = useAddToList({
        inventoryItem,
        initialListsInclusions: listsInclusions,
        onAddToList: handleAddToList,
      });

      return (
        <>
          <Button
            data-testid="add-to-list-button"
            elevation={elevation}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              handleOnToggleList();
            }}
            circle
            size={size}
            iconAfter={<FavoriteIcon favorited={itemIsFavorited} size={size} />}
            aria-label={intl.formatMessage(
              itemIsFavorited ? messages.removeFromList : messages.addToList
            )}
            title={intl.formatMessage(
              itemIsFavorited ? messages.removeFromList : messages.addToList
            )}
          />
          {ListModal}
        </>
      );
    }
  );
